import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { IconChevronRight, SkeletonLabel } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import * as styles from 'src/components/organisms/ProductReviews/components/ProductReviews/ProductReviews.styles';
import { ERROR_REVIEWS, NO_REVIEWS } from 'src/components/organisms/ProductReviews/constants/copy';
import { getReviewsDrawerURLFragment } from 'src/fragmentIDs';
import type { ProductReviewsState } from 'src/redux/reviews/slice';
import { FeaturedReview } from '../ProductReview/FeaturedReview';

const ProductReviewsDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_returns_drawer" */ '../ProductReviewDrawer/ProductReviewsDrawer'
  ).then((mod) => mod.ProductReviewsDrawer),
);

export interface ProductReviewsProps {
  onVisible: () => void;
  productCode: number;
  reviews: ProductReviewsState;
}

export const ProductReviews = ({ onVisible, productCode, reviews }: ProductReviewsProps) => {
  const title =
    reviews.pagination?.totalEntries === 1
      ? '1 Product review'
      : `${reviews.pagination?.totalEntries} Product reviews`;

  const { error, pages, pagination } = reviews;
  const productReviewsRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  const buttonText =
    pagination?.totalEntries === 1 ? 'See 1 review' : `See all ${pagination?.totalEntries} reviews`;
  const reviewsPlaceholderItems = Array.from({ length: 3 });

  if (pages === null) {
    return (
      <div css={styles.placeholderContainer}>
        {reviewsPlaceholderItems.map((_, index) => (
          <SkeletonLabel height={52} key={`skeleton-${index}`} labelHeight="XL" />
        ))}
      </div>
    );
  }

  if (error && Object.keys(pages).length === 0) {
    return <p css={styles.noReviews}>{ERROR_REVIEWS}</p>;
  }

  if (pages && pages[1]?.length === 0) {
    return <p css={styles.noReviews}>{NO_REVIEWS}</p>;
  }

  return (
    <div css={styles.reviewsContainer} ref={productReviewsRef}>
      <div css={styles.scrollable}>
        <ul
          css={styles.reviewsList}
          style={
            {
              '--listWidth': pages[1].length === 1 ? '100%' : '760px',
            } as styles.ReviewItemCSS
          }
        >
          {pages[1].map((review) => (
            <FeaturedReview
              href={`#${getReviewsDrawerURLFragment(review.id)}`}
              key={`review-${review.id}`}
              review={review}
            />
          ))}
        </ul>
      </div>
      <a css={styles.drawerLink} href={`#${getReviewsDrawerURLFragment()}`}>
        {buttonText}
        <IconChevronRight color="var(--brand-purple-base)" height={16} width={16} />
      </a>
      <ProductReviewsDrawer productCode={productCode} title={title} />
    </div>
  );
};
